import * as Sentry from '@sentry/react';
import { useMemo, useRef } from 'react';
import type { FocusData } from '../../../../../api/initiative-client/initiative-client.type';
import type { Board, Team } from '../../../../../api/projects-client/projects-client.type';
import { useFocusTabStore, useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import { useCurrentStrategyTab } from '../../../strategy.hooks';
import { StrategyTab } from '../../../strategy.type';
import { CHART_COLORS } from '../../initiative-performance.constants';
import { FocusMode } from '../../initiative-performance.type';
import type { ChartDataItem } from './focus-chart.type';

/**
 * Hook to retrieve the appropriate focus data based on the current mode
 *
 * @returns Focus data for either teams or boards depending on the selected mode,
 *          or null if no data is available
 */
const useFocusData = (): FocusData | null => {
  const currentTab = useCurrentStrategyTab();
  const initiative = useStrategyStore((state) => state.initiativePerformance) as any;
  const mode = useFocusTabStore((state) => state.mode);
  const focusTeamsData = useFocusTabStore((state) => state.focusTeamsData);
  const focusBoardsData = useFocusTabStore((state) => state.focusBoardsData);

  switch (mode) {
    case FocusMode.ContributingTeams:
      if (currentTab === StrategyTab.Initiatives) {
        return focusTeamsData;
      }
      return initiative?.focus_by_team_over_time || {};
    case FocusMode.ContributingBoards:
      if (currentTab === StrategyTab.Initiatives) {
        return focusBoardsData;
      }
      return initiative?.focus_by_board_over_time || {};
    default:
      return null;
  }
};

/**
 * Hook to retrieve the selected teams or boards based on the current mode
 *
 * @returns Array of selected teams or boards, or an empty array if no selection is made
 */
const useSelection = (): Team[] | Board[] | [] => {
  const mode = useFocusTabStore((state) => state.mode);
  const selectedTeams = useFocusTabStore((state) => state.selectedTeams);
  const selectedBoards = useFocusTabStore((state) => state.selectedBoards);

  if (mode === FocusMode.ContributingTeams) {
    return selectedTeams;
  }

  if (mode === FocusMode.ContributingBoards) {
    return selectedBoards;
  }

  return [];
};

/**
 * Hook to generate chart data for team focus visualization
 *
 * @returns An array of chart data items containing date and focus values for each team
 */
const useChartData = (): ChartDataItem[] | [] => {
  const focusData = useFocusData();
  const selection = useSelection();
  const metric = useFocusTabStore((state) => state.metric);

  return useMemo(() => {
    try {
      if (!focusData || !selection.length) {
        return [];
      }

      const allDates = new Set<string>();
      selection.forEach((item) => {
        const itemId = item.id;
        if (focusData[itemId]?.[metric]) {
          Object.keys(focusData[itemId]?.[metric] || {}).forEach((date) => {
            allDates.add(date);
          });
        }
      });

      const sortedDates = Array.from(allDates).sort();

      return sortedDates.map((date) => {
        const dataPoint: ChartDataItem = {
          date: date,
        };

        selection.forEach((item) => {
          const itemId = item.id;
          dataPoint[itemId] = focusData[itemId]?.[metric]?.[date] || 0;
        });

        return dataPoint;
      });
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }, [focusData, metric, selection]);
};

/**
 * Get selection colors for visualization
 *
 * @param selection - Array of selection to generate colors for
 * @returns Record mapping selection IDs to color values
 */
const useSelectionColors = (selection: Team[] | Board[]): Record<string, string> => {
  // Use ref to persist color assignments across renders
  const colorAssignmentsRef = useRef<Record<string, string>>({});

  return useMemo(() => {
    try {
      const teamColors: Record<string, string> = {};

      selection.forEach((item, index) => {
        // Use previously assigned color if it exists
        if (colorAssignmentsRef.current[item.id]) {
          teamColors[item.id] = colorAssignmentsRef.current[item.id];
        } else {
          // Assign a new color if not previously assigned
          teamColors[item.id] = CHART_COLORS[index % CHART_COLORS.length];
          colorAssignmentsRef.current[item.id] = teamColors[item.id];
        }
      });

      return teamColors;
    } catch (error) {
      Sentry.captureException(error);
      return {};
    }
  }, [selection]);
};

export { useChartData, useFocusData, useSelection, useSelectionColors };
